import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, ExternalLink, CheckSquare, Calculator, ClipboardList, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getChecklistTemplates, saveChecklistTemplate, deleteToolItem, GetChecklistTemplatesOutputType } from 'zite-endpoints-sdk';

type Template = GetChecklistTemplatesOutputType['templates'][0];

type ChecklistItem = { id: string; text: string; category: string; required: boolean };

const CHECKLIST_TYPES = ['Opening Checks', 'Closing Checks', 'Bar Checks', 'Fire Checks', 'Cleaning', 'Kitchen Handover', 'Cellar Checks', 'Event Checks', 'Health & Safety', 'Custom'];
const MIN_ROLES = ['Staff', 'Supervisor', 'Manager', 'Admin'];

const DEFAULT_TEMPLATES: Array<{ title: string; type: string; minRole: string; requiresSignoff: boolean; items: ChecklistItem[] }> = [
  { title: 'Opening Checks', type: 'Opening Checks', minRole: 'Staff', requiresSignoff: false, items: [
    { id: '1', text: 'Tills turned on and correct float loaded', category: 'Finance', required: true },
    { id: '2', text: 'Bar stocked and fridges checked', category: 'Bar', required: true },
    { id: '3', text: 'Glassware polished and racked', category: 'Bar', required: true },
    { id: '4', text: 'Menus out and up to date', category: 'Front of House', required: true },
    { id: '5', text: 'Specials board updated', category: 'Front of House', required: true },
    { id: '6', text: 'Toilets checked and stocked', category: 'Front of House', required: true },
    { id: '7', text: 'Music / lighting set', category: 'Ambience', required: false },
    { id: '8', text: 'All fire exits clear', category: 'Safety', required: true },
    { id: '9', text: 'Outdoor area checked', category: 'Front of House', required: false },
    { id: '10', text: 'Staff brief completed', category: 'Team', required: true },
  ]},
  { title: 'Closing Checks', type: 'Closing Checks', minRole: 'Manager', requiresSignoff: true, items: [
    { id: '1', text: 'Tills cashed up and reports printed', category: 'Finance', required: true },
    { id: '2', text: 'All staff signed out', category: 'Team', required: true },
    { id: '3', text: 'Bar stripped and cleaned', category: 'Bar', required: true },
    { id: '4', text: 'All food stored correctly and fridges at temp', category: 'Kitchen', required: true },
    { id: '5', text: 'Cellar closed and secured', category: 'Cellar', required: true },
    { id: '6', text: 'Fire check complete', category: 'Safety', required: true },
    { id: '7', text: 'Waste taken out', category: 'Cleaning', required: true },
    { id: '8', text: 'Building locked and alarmed', category: 'Security', required: true },
    { id: '9', text: 'End of shift report completed', category: 'Finance', required: true },
  ]},
  { title: 'Bar Checks', type: 'Bar Checks', minRole: 'Staff', requiresSignoff: false, items: [
    { id: '1', text: 'Beer lines checked and pressures correct', category: 'Cellar', required: true },
    { id: '2', text: 'Spirits at correct levels and aligned', category: 'Bar', required: true },
    { id: '3', text: 'Wine chilled to correct temperature', category: 'Bar', required: true },
    { id: '4', text: 'Ice machine on and stocked', category: 'Bar', required: true },
    { id: '5', text: 'Bar fruit and garnishes prepped', category: 'Bar', required: true },
    { id: '6', text: 'Mixers stocked', category: 'Bar', required: true },
    { id: '7', text: 'Glassware polished and at station', category: 'Bar', required: true },
    { id: '8', text: 'Cocktail ingredients prepped', category: 'Bar', required: false },
    { id: '9', text: 'Fridges at correct temp', category: 'Safety', required: true },
  ]},
  { title: 'Fire Checks', type: 'Fire Checks', minRole: 'Manager', requiresSignoff: true, items: [
    { id: '1', text: 'All fire exits clear and unlocked', category: 'Fire Safety', required: true },
    { id: '2', text: 'Fire extinguishers in place and in date', category: 'Fire Safety', required: true },
    { id: '3', text: 'Fire alarm panel checked', category: 'Fire Safety', required: true },
    { id: '4', text: 'Emergency lighting operational', category: 'Fire Safety', required: true },
    { id: '5', text: 'Evacuation routes clear', category: 'Fire Safety', required: true },
    { id: '6', text: 'Fire log updated', category: 'Fire Safety', required: true },
  ]},
  { title: 'Cellar Checks', type: 'Cellar Checks', minRole: 'Supervisor', requiresSignoff: false, items: [
    { id: '1', text: 'Cellar temperature checked (11-13°C)', category: 'Cellar', required: true },
    { id: '2', text: 'Gas levels checked', category: 'Cellar', required: true },
    { id: '3', text: 'Lines checked for clarity', category: 'Cellar', required: true },
    { id: '4', text: 'Stock rotation checked', category: 'Cellar', required: true },
    { id: '5', text: 'Drip trays cleaned', category: 'Cellar', required: true },
    { id: '6', text: 'Empty kegs removed', category: 'Cellar', required: false },
  ]},
];

export default function ToolsAdminTab() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState<Template | null>(null);
  const [form, setForm] = useState({ title: '', type: 'Opening Checks', minRole: 'Staff' as const, requiresSignoff: false, active: true });
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [newItemCat, setNewItemCat] = useState('General');
  const [isSaving, setIsSaving] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    getChecklistTemplates({}).then(d => setTemplates(d.templates)).finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditTemplate(null);
    setForm({ title: '', type: 'Opening Checks', minRole: 'Staff', requiresSignoff: false, active: true });
    setItems([]);
    setSheetOpen(true);
  };

  const openEdit = (t: Template) => {
    setEditTemplate(t);
    setForm({ title: t.title, type: t.type, minRole: t.minRole as any, requiresSignoff: t.requiresSignoff, active: t.active });
    try { setItems(JSON.parse(t.items)); } catch { setItems([]); }
    setSheetOpen(true);
  };

  const addItem = () => {
    if (!newItemText.trim()) return;
    setItems(p => [...p, { id: Date.now().toString(), text: newItemText.trim(), category: newItemCat, required: false }]);
    setNewItemText('');
  };

  const removeItem = (id: string) => setItems(p => p.filter(i => i.id !== id));

  const toggleRequired = (id: string) =>
    setItems(p => p.map(i => i.id === id ? { ...i, required: !i.required } : i));

  const handleSave = async () => {
    if (!form.title.trim() || items.length === 0) { toast.error('Title and at least one item required'); return; }
    setIsSaving(true);
    try {
      await saveChecklistTemplate({ id: editTemplate?.id, ...form, items: JSON.stringify(items) });
      toast.success(editTemplate ? 'Template updated' : 'Template created');
      setSheetOpen(false);
      load();
    } catch { toast.error('Failed to save'); } finally { setIsSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteToolItem({ type: 'template', id, action: 'delete' });
      toast.success('Template deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  const seedDefaults = async () => {
    setIsSeeding(true);
    try {
      for (const t of DEFAULT_TEMPLATES) {
        await saveChecklistTemplate({ title: t.title, type: t.type, minRole: t.minRole as any, requiresSignoff: t.requiresSignoff, active: true, items: JSON.stringify(t.items) });
      }
      toast.success(`Seeded ${DEFAULT_TEMPLATES.length} default checklist templates!`);
      load();
    } catch { toast.error('Failed to seed templates'); } finally { setIsSeeding(false); }
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="checklists">
        <TabsList className="mb-4">
          <TabsTrigger value="checklists"><CheckSquare className="w-4 h-4 mr-1.5" /> Checklist Templates</TabsTrigger>
          <TabsTrigger value="tools"><Calculator className="w-4 h-4 mr-1.5" /> Staff Tools</TabsTrigger>
        </TabsList>

        {/* Checklist Template Manager */}
        <TabsContent value="checklists">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-base">Checklist Templates</h2>
              <p className="text-sm text-muted-foreground">Create and manage checklists for all staff roles</p>
            </div>
            <div className="flex gap-2">
              {templates.length === 0 && (
                <Button variant="outline" size="sm" onClick={seedDefaults} disabled={isSeeding}>
                  {isSeeding ? 'Seeding...' : '🌱 Seed Defaults'}
                </Button>
              )}
              <Button size="sm" onClick={openNew}><Plus className="w-4 h-4 mr-1.5" /> New Template</Button>
            </div>
          </div>

          {loading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
          ) : templates.length === 0 ? (
            <Card className="p-10 text-center">
              <CheckSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <h3 className="font-semibold mb-1">No checklist templates yet</h3>
              <p className="text-sm text-muted-foreground mb-4">Seed the defaults to get started with standard pub checklists, or create your own.</p>
              <Button onClick={seedDefaults} disabled={isSeeding}>{isSeeding ? 'Seeding...' : '🌱 Seed Default Templates'}</Button>
            </Card>
          ) : (
            <div className="space-y-2">
              {templates.map(t => (
                <div key={t.id} className="flex items-center gap-3 p-3 border border-border rounded-xl hover:bg-muted/30 transition-colors">
                  <CheckSquare className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{t.title}</p>
                    <p className="text-xs text-muted-foreground">{t.type} · Min role: {t.minRole} · {(() => { try { return JSON.parse(t.items).length; } catch { return 0; } })() } items</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.requiresSignoff && <Badge variant="outline" className="text-xs">Signoff</Badge>}
                    <Badge variant={t.active ? 'default' : 'outline'} className="text-xs">{t.active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)}><Pencil className="w-3.5 h-3.5" /></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost"><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete "{t.title}"?</AlertDialogTitle></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(t.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Staff Tools Quick Links */}
        <TabsContent value="tools">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'GP Calculator', desc: 'Calculate gross profit for beer, wine, spirits, cocktails and more', icon: Calculator, href: '/gp-calculator', color: 'text-blue-500', bg: 'bg-blue-500/10' },
              { title: 'Shift Prep Builder', desc: 'Build printable shift prep sheets with weather and staff plans', icon: ClipboardList, href: '/shift-prep', color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
              { title: 'Event Setup Builder', desc: 'Create event setup sheets, run sheets and staff briefings', icon: Calendar, href: '/event-setup', color: 'text-purple-500', bg: 'bg-purple-500/10' },
              { title: 'All Checklists', desc: 'Complete and manage operational checklists', icon: CheckSquare, href: '/checklists', color: 'text-amber-500', bg: 'bg-amber-500/10' },
            ].map(t => {
              const Icon = t.icon;
              return (
                <Card key={t.title} className="p-4 hover:border-primary/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`p-2.5 rounded-xl ${t.bg} shrink-0`}><Icon className={`w-5 h-5 ${t.color}`} /></div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm mb-0.5">{t.title}</h3>
                      <p className="text-xs text-muted-foreground mb-3">{t.desc}</p>
                      <Link to={t.href}><Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open Tool</Button></Link>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          <div className="mt-4 p-4 bg-muted/40 border border-border rounded-xl text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">All tools are available from the main navigation</p>
            <p>Staff access GP calculators and checklists. Supervisors and above can use Shift Prep and Event Setup builders. Managers and Admins can manage checklist templates from the Checklists tab above.</p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Template Editor Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="mb-4"><SheetTitle>{editTemplate ? 'Edit Template' : 'New Checklist Template'}</SheetTitle></SheetHeader>
          <div className="space-y-4">
            <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="e.g. Opening Checks" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={v => setForm(p => ({...p, type: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHECKLIST_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Minimum Role</Label>
                <Select value={form.minRole} onValueChange={v => setForm(p => ({...p, minRole: v as any}))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{MIN_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex items-center gap-2"><Checkbox id="signoff" checked={form.requiresSignoff} onCheckedChange={v => setForm(p => ({...p, requiresSignoff: !!v}))} /><label htmlFor="signoff" className="text-sm cursor-pointer">Requires manager sign-off</label></div>
              <div className="flex items-center gap-2"><Checkbox id="active" checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: !!v}))} /><label htmlFor="active" className="text-sm cursor-pointer">Active</label></div>
            </div>

            <div className="border-t border-border pt-4">
              <Label className="mb-2 block">Checklist Items ({items.length})</Label>
              <div className="space-y-1.5 mb-3 max-h-48 overflow-y-auto">
                {items.map((item, idx) => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-muted/40 rounded-lg text-sm">
                    <span className="text-muted-foreground text-xs w-5 shrink-0">{idx + 1}.</span>
                    <span className="flex-1 truncate">{item.text}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{item.category}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <Checkbox id={`req-${item.id}`} checked={item.required} onCheckedChange={() => toggleRequired(item.id)} className="w-3.5 h-3.5" />
                      <label htmlFor={`req-${item.id}`} className="text-xs text-muted-foreground cursor-pointer">req</label>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(item.id)} className="h-6 w-6 p-0"><Trash2 className="w-3 h-3 text-destructive" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newItemText} onChange={e => setNewItemText(e.target.value)} placeholder="Add item..." className="flex-1" onKeyDown={e => e.key === 'Enter' && addItem()} />
                <Input value={newItemCat} onChange={e => setNewItemCat(e.target.value)} placeholder="Category" className="w-28" />
                <Button size="sm" variant="outline" onClick={addItem} disabled={!newItemText.trim()}><Plus className="w-3.5 h-3.5" /></Button>
              </div>
            </div>

            <Button onClick={handleSave} disabled={isSaving} className="w-full">
              {isSaving ? 'Saving...' : editTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
